import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Badge, Button, Input, Label, Switch } from "@/components/ui";
import { ConfirmDialog } from "@/components/common";
import { generateSku } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { useAddVariant, useDeleteVariant, useUpdateVariant } from "../api";
import type { Product, ProductVariant } from "@/types";

interface ProductVariantsEditorProps {
  product: Product;
}

type VariantDraft = {
  name: string;
  sku: string;
  price: string;
  costPrice: string;
  isActive: boolean;
};

function draftFrom(variant: ProductVariant): VariantDraft {
  return {
    name: variant.name,
    sku: variant.sku,
    price: (variant.price / 100).toFixed(2),
    costPrice: (variant.costPrice / 100).toFixed(2),
    isActive: variant.isActive,
  };
}

function draftToPayload(draft: VariantDraft) {
  return {
    name: draft.name,
    sku: draft.sku,
    price: Math.round((parseFloat(draft.price) || 0) * 100),
    costPrice: Math.round((parseFloat(draft.costPrice) || 0) * 100),
    isActive: draft.isActive,
  };
}

function isDirty(variant: ProductVariant, draft: VariantDraft): boolean {
  const payload = draftToPayload(draft);

  return (
    payload.name !== variant.name ||
    payload.sku !== variant.sku ||
    payload.price !== variant.price ||
    payload.costPrice !== variant.costPrice ||
    payload.isActive !== variant.isActive
  );
}

export function ProductVariantsEditor({ product }: ProductVariantsEditorProps) {
  const addVariant = useAddVariant();
  const updateVariant = useUpdateVariant();
  const deleteVariant = useDeleteVariant();

  const variants = useMemo(() => product.variants ?? [], [product.variants]);

  const [drafts, setDrafts] = useState<Record<string, VariantDraft>>(() =>
    Object.fromEntries(variants.map((v) => [v.id, draftFrom(v)])),
  );

  const [newVariant, setNewVariant] = useState({
    name: "",
    sku: "",
    price: 0,
    costPrice: 0,
  });

  const variantSkuFor = (name: string) =>
    name ? generateSku(name, product.sku) : "";

  const handleNewVariantName = (name: string) => {
    setNewVariant((prev) => ({
      ...prev,
      name,
      sku:
        prev.sku === "" || prev.sku === variantSkuFor(prev.name)
          ? variantSkuFor(name)
          : prev.sku,
    }));
  };

  const [deleteCandidate, setDeleteCandidate] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const draftFor = (variant: ProductVariant): VariantDraft =>
    drafts[variant.id] ?? draftFrom(variant);

  const setDraftField = (
    variant: ProductVariant,
    field: keyof VariantDraft,
    value: string | boolean,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [variant.id]: { ...draftFor(variant), [field]: value },
    }));
  };

  const dirtyVariants = variants.filter((v) =>
    drafts[v.id] ? isDirty(v, drafts[v.id]!) : false,
  );

  const handleSaveChanges = async () => {
    const invalid = dirtyVariants.find(
      (v) => !draftFor(v).name.trim() || !draftFor(v).sku.trim(),
    );

    if (invalid) {
      notify.error("Variant name and SKU cannot be empty");

      return;
    }

    try {
      for (const variant of dirtyVariants) {
        await updateVariant.mutateAsync({
          variantId: variant.id,
          data: draftToPayload(draftFor(variant)),
        });
      }

      notify.success(
        dirtyVariants.length === 1
          ? "Variant saved"
          : `${dirtyVariants.length} variants saved`,
      );
    } catch (err) {
      notify.fromError(err, "Failed to save variants");
    }
  };

  const handleDiscard = () => {
    setDrafts(Object.fromEntries(variants.map((v) => [v.id, draftFrom(v)])));
  };

  const handleAdd = async () => {
    if (!newVariant.name || !newVariant.sku) {
      notify.error("Please enter variant name and SKU");

      return;
    }

    try {
      await addVariant.mutateAsync({
        productId: product.id,
        data: {
          name: newVariant.name,
          sku: newVariant.sku,
          price: Math.round(newVariant.price * 100),
          costPrice: Math.round(newVariant.costPrice * 100),
          isDefault: false,
        },
      });
      notify.success("Variant added");
      setNewVariant({ name: "", sku: "", price: 0, costPrice: 0 });
    } catch (err) {
      notify.fromError(err, "Failed to add variant");
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) {
      return;
    }

    try {
      await deleteVariant.mutateAsync(deleteCandidate.id);
      notify.success("Variant deleted");
      setDeleteCandidate(null);
    } catch (err) {
      notify.fromError(err, "Failed to delete variant");
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="space-y-3">
        {variants.map((variant) => {
          const draft = draftFor(variant);
          const dirty = isDirty(variant, draft);

          return (
            <div
              key={variant.id}
              className={`rounded-md border p-4 ${dirty ? "border-primary/50" : ""}`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`variant-name-${variant.id}`}
                      className="whitespace-nowrap"
                    >
                      Name
                    </Label>
                    <Input
                      id={`variant-name-${variant.id}`}
                      value={draft.name}
                      onChange={(e) =>
                        setDraftField(variant, "name", e.target.value)
                      }
                      placeholder="e.g., Small"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`variant-sku-${variant.id}`}
                      className="whitespace-nowrap"
                    >
                      SKU
                    </Label>
                    <Input
                      id={`variant-sku-${variant.id}`}
                      value={draft.sku}
                      onChange={(e) =>
                        setDraftField(variant, "sku", e.target.value)
                      }
                      placeholder="e.g., BRK-001-S"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`variant-price-${variant.id}`}
                      className="whitespace-nowrap"
                    >
                      Selling Price ($)
                    </Label>
                    <Input
                      id={`variant-price-${variant.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={draft.price}
                      onChange={(e) =>
                        setDraftField(variant, "price", e.target.value)
                      }
                      placeholder="e.g., 4.99"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`variant-cost-${variant.id}`}
                      className="whitespace-nowrap"
                    >
                      Cost to Make ($)
                    </Label>
                    <Input
                      id={`variant-cost-${variant.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={draft.costPrice}
                      onChange={(e) =>
                        setDraftField(variant, "costPrice", e.target.value)
                      }
                      placeholder="e.g., 1.50"
                    />
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3 pb-1">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id={`variant-active-${variant.id}`}
                      checked={draft.isActive}
                      onCheckedChange={(checked) =>
                        setDraftField(variant, "isActive", checked)
                      }
                    />
                    <Label
                      htmlFor={`variant-active-${variant.id}`}
                      className="text-xs text-muted-foreground"
                    >
                      Active
                    </Label>
                  </div>
                  {variant.isDefault && (
                    <Badge variant="outline" className="text-xs">
                      Default
                    </Badge>
                  )}
                  <Badge
                    variant="secondary"
                    className={`text-xs ${dirty ? "" : "invisible"}`}
                  >
                    Unsaved
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setDeleteCandidate({ id: variant.id, name: variant.name })
                    }
                    disabled={deleteVariant.isPending || variants.length <= 1}
                    title={
                      variants.length <= 1
                        ? "Cannot delete the only variant"
                        : "Delete variant"
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2">
        {dirtyVariants.length > 0 && (
          <Button type="button" variant="outline" onClick={handleDiscard}>
            Discard Changes
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSaveChanges}
          disabled={dirtyVariants.length === 0 || updateVariant.isPending}
        >
          {updateVariant.isPending
            ? "Saving..."
            : dirtyVariants.length > 0
              ? `Save Changes (${dirtyVariants.length})`
              : "Save Changes"}
        </Button>
      </div>
      <div className="space-y-3 rounded-md border border-dashed p-4">
        <p className="font-medium">Add New Variant</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-variant-name" className="whitespace-nowrap">
                Name
              </Label>
              <Input
                id="new-variant-name"
                value={newVariant.name}
                onChange={(e) => handleNewVariantName(e.target.value)}
                placeholder="e.g., Large"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-variant-sku" className="whitespace-nowrap">
                SKU
              </Label>
              <Input
                id="new-variant-sku"
                value={newVariant.sku}
                onChange={(e) =>
                  setNewVariant({ ...newVariant, sku: e.target.value })
                }
                placeholder="Auto-generated from name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-variant-price" className="whitespace-nowrap">
                Selling Price ($)
              </Label>
              <Input
                id="new-variant-price"
                type="number"
                step="0.01"
                min="0"
                value={newVariant.price || ""}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    price: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="e.g., 4.99"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-variant-cost" className="whitespace-nowrap">
                Cost to Make ($)
              </Label>
              <Input
                id="new-variant-cost"
                type="number"
                step="0.01"
                min="0"
                value={newVariant.costPrice || ""}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    costPrice: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="e.g., 1.50"
              />
            </div>
          </div>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={addVariant.isPending}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={!!deleteCandidate}
        onOpenChange={(next) => {
          if (!next) {
            setDeleteCandidate(null);
          }
        }}
        title="Delete variant?"
        description={
          <>
            This will remove{" "}
            <span className="font-medium text-foreground">
              {deleteCandidate?.name}
            </span>{" "}
            from the product. Past orders that referenced it will still show
            its name on the receipt.
          </>
        }
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        destructive
        loading={deleteVariant.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
